/**
 * Shared primitives for the custom overlay draw callbacks (fibonacci, risk /
 * reward, measure, bar-highlight). Each of these was previously copy-pasted per
 * overlay file:
 *
 *  - `isNum`               — finite-number guard on `overlay.points[i].value`
 *  - `OverlayFigure`       — the figure shape klinecharts' `createPointFigures`
 *                            returns (was a local `type Figure = {…}` in 3 files)
 *  - `resolvePricePrecision` — candle-pane vs sub-pane price precision (the
 *                            `isInCandle` ternary, duplicated 4×)
 */

import type { OverlayFigureIgnoreEventType } from 'klinecharts'

/** Narrow, finite-number guard — rejects `NaN`/`Infinity`/non-numbers. */
export function isNum(v: unknown): v is number {
  return typeof v === 'number' && isFinite(v)
}

/** The figure descriptor klinecharts' overlay `createPointFigures` returns. */
export type OverlayFigure = {
  /**
   * Stable role tag. klinecharts echoes this back as `figureKey` on overlay
   * mouse events, which is how cursor feedback identifies WHAT is hovered —
   * far safer than indexing into the returned array, whose shape changes
   * between hover states.
   */
  key?: string
  type: string
  attrs: unknown
  styles?: unknown
  ignoreEvent?: boolean | OverlayFigureIgnoreEventType[]
}

/** The subset of klinecharts' YAxis the overlays read. `isInCandle()` reports
 *  whether the cursor's pane is the candle pane (→ price precision) or a
 *  sub-pane (→ excludePriceVolumeMax). */
export interface OverlayYAxisLike {
  isInCandle?: () => boolean
}

/** The subset of klinecharts' precision bundle the overlays read. */
export interface OverlayPrecision {
  price: number
  excludePriceVolumeMax: number
}

/**
 * Price decimal precision for an overlay label: the candle pane's `price`
 * precision when the cursor is in the candle pane (the default when `yAxis` or
 * `isInCandle` is absent), else the sub-pane's `excludePriceVolumeMax`. Mirrors
 * klinecharts' own default-figure precision logic.
 */
export function resolvePricePrecision(
  yAxis: OverlayYAxisLike | null | undefined,
  precision: OverlayPrecision,
): number {
  return (yAxis?.isInCandle?.() ?? true) ? precision.price : precision.excludePriceVolumeMax
}

/* ------------------------------------------------------------------ */
/*  Figure builders — shared by every custom overlay template.          */
/*  Promoted out of riskRewardOverlay.ts so the shape/arrow/measure     */
/*  templates build against one implementation instead of copies.       */
/* ------------------------------------------------------------------ */

export interface Coord {
  x: number
  y: number
}

/**
 * Axis-aligned rectangle from two arbitrary corners.
 *
 * Normalises direction, so a drag up-and-left yields the same figure as the
 * equivalent drag down-and-right — required for click-drag shape creation,
 * where the user's second point may be above/left of the first.
 */
export function rectFigure(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  styles: { color?: string; borderColor?: string; borderSize?: number; style?: string },
  draggable = false,
): OverlayFigure {
  return {
    type: 'rect',
    ignoreEvent: !draggable,
    attrs: {
      x: Math.min(x1, x2),
      y: Math.min(y1, y2),
      width: Math.abs(x2 - x1),
      height: Math.abs(y2 - y1),
    },
    styles,
  }
}

/** Straight line between two coordinates. */
export function lineFigure(
  from: Coord,
  to: Coord,
  opts: { color?: string; size?: number; dashed?: boolean },
  draggable = false,
): OverlayFigure {
  const { color, size, dashed } = opts
  const styles: Record<string, unknown> = {}
  if (color !== undefined) styles.color = color
  if (size !== undefined) styles.size = size
  if (dashed) {
    styles.style = 'dashed'
    styles.dashedValue = [4, 4]
  }
  return {
    type: 'line',
    ignoreEvent: !draggable,
    attrs: { coordinates: [from, to] },
    styles,
  }
}
